// src/modules/app-views/services/pages/Document/tabs/ParametersTab/modals/ProcedureStepModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProcedureStepModal = ({ open, onCancel, onSave, initialData, loading }) => {
  // ---------------------------------------------------------------------------
  // 1. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // 2. LOCAL VARIABLES
  // ---------------------------------------------------------------------------
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_step') : translateX('new_step');

  // ---------------------------------------------------------------------------
  // 3. EFFECTS
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          phase: initialData.phase || '',
          duration: initialData.duration || '',
          description: initialData.description || '',
          actions: initialData.actions?.length ? initialData.actions : [],
        });
      } else {
        form.resetFields();
      }

      // auto-focus on phase field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ---------------------------------------------------------------------------
  // 4. HANDLERS
  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      // filter out empty action strings
      const actions = (values.actions || [])
        .map(a => (a || '').trim())
        .filter(a => a.length > 0);

      onSave({
        phase: (values.phase || '').trim(),
        duration: (values.duration || '').trim(),
        description: (values.description || '').trim(),
        actions,
      });
    } catch (error) {
      // validation failed - form will show errors
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      getContainer={document.body}
      width={500}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ phase: '', duration: '', description: '', actions: [] }}
      >
        <Form.Item
          name="phase"
          label={translateX('phase')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={60}
            placeholder="Diagnostico Inicial"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="duration"
          label={translateX('duration')}
        >
          <Input
            maxLength={30}
            placeholder="15 minutos"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={translateX('description')}
        >
          <Input.TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder={translateX('description')}
            disabled={loading}
          />
        </Form.Item>

        {/* dynamic list of action items */}
        <Form.Item label={translateX('actions')}>
          <Form.List name="actions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Flex key={key} gap={8} align="baseline" style={{ marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        placeholder={translateX('action')}
                        maxLength={200}
                        disabled={loading}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                      disabled={loading}
                    />
                  </Flex>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  disabled={loading}
                >
                  {translateX('add_action')}
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProcedureStepModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
